import mixpanel, {
  type Callback,
  type Dict,
  type RequestOptions,
} from 'mixpanel-browser'

class MixpanelTracker {
  #debug = import.meta.env.VITE_MIXPANEL_DEBUG === 'true'
  #enabled = false

  #log(method: string, ...args: unknown[]): void {
    if (!this.#debug) return
    // eslint-disable-next-line no-console -- intentional debug logging for tracker diagnostics
    console.info(`[Mixpanel] ${method}`, ...args)
  }

  init(): void {
    this.#log('init')
    if (this.#debug) return
    const token = import.meta.env.VITE_MIXPANEL_PROJECT_TOKEN
    if (!token) return

    mixpanel.init(token, {
      batch_requests: true,
      ignore_dnt: true,
      record_sessions_percent: 10,
    })
    this.#enabled = true
  }

  track(
    eventName: string,
    properties?: Dict,
    optionsOrCallback?: RequestOptions | Callback,
    callback?: Callback,
  ): void {
    const enriched = { ...properties, date: new Date().toISOString() }
    this.#log('track', eventName, enriched)
    if (!this.#enabled) return
    mixpanel.track(eventName, enriched, optionsOrCallback, callback)
  }

  set(prop: string | Dict, to?: unknown, callback?: Callback): void {
    this.#log('people.set', prop, to)
    if (!this.#enabled) return
    if (typeof prop === 'string') {
      mixpanel.people.set(prop, to, callback)
    } else {
      mixpanel.people.set(prop, to as Callback | undefined)
    }
  }

  identify(uniqueId?: string): void {
    this.#log('identify', uniqueId)
    if (!this.#enabled) return
    mixpanel.identify(uniqueId)
  }

  setGroup(
    groupKey: string,
    groupId: string | string[] | number | number[],
  ): void {
    this.#log('set_group', groupKey, groupId)
    if (!this.#enabled) return
    mixpanel.set_group(groupKey, groupId)
  }

  setGroupProperties(groupKey: string, groupId: string, props: Dict): void {
    this.#log('get_group.set', groupKey, groupId, props)
    if (!this.#enabled) return
    mixpanel.get_group(groupKey, groupId).set(props)
  }

  reset(): void {
    this.#log('reset')
    if (!this.#enabled) return
    mixpanel.reset()
  }

  register(props: Dict): void {
    this.#log('register', props)
    if (!this.#enabled) return
    mixpanel.register(props)
  }
}

export default new MixpanelTracker()
